import {Injectable} from "@angular/core";
import {CartLine} from "@app/core/cart/domain/dtos/cart-line";
import {forkJoin, Observable} from "rxjs";
import {map, take} from "rxjs/operators";
import {CheckStockRequest} from "../domain/dtos/check-stock-request";
import {CheckStockResponse} from "../domain/dtos/check-stock-response";
import {CheckStockResult} from "../domain/dtos/check-stock-result";
import {CheckStockRemote} from "../domain/repositories/check-stock.remote";

/**
 * Use case for checking the stock availability of provided cart lines.
 */
@Injectable({
    providedIn: "root"
})
export class CheckStockUseCase {

    constructor(
        private checkStockRemote: CheckStockRemote
    ) { }

    /**
     * Executes the stock check for the provided cart lines.
     *
     * @param cartLines - The list of cart lines to check for stock availability.
     * @returns An observable that emits the result categorizing the products into those with stock and those without stock.
     */
    execute(cartLines: CartLine[]): Observable<CheckStockResult> {
        // Transform cartLines into a CheckStockRequest array
        const checkStockRequests = cartLines.map(
            cartLine => ({ product: cartLine, quantity: cartLine.quantity } as CheckStockRequest)
        )

        const observableList = checkStockRequests.map(
            // Build an observable list with each web service call
            stockRequest => this.checkStockRemote.checkStock(stockRequest.product).pipe(
                take(1),
                map(
                    // Check if the existing stock is enough to satisfy the requested quantity
                    quantity => {
                        return {
                            product: stockRequest.product,
                            hasStock: quantity >= stockRequest.quantity,
                        } as CheckStockResponse
                    }
                )
            )
        )

        // Join all the observables in the list into a new observable 
        // whose content is an array of the other observables' results
        return forkJoin(observableList).pipe(
            map(
                // Transform the stock responses into a CheckStockResult
                stockResponses => ({
                    withStock: [...stockResponses]
                        .filter(item => item.hasStock)
                        .map(item => item.product),
                    withoutStock: [...stockResponses]
                        .filter(item => !item.hasStock)
                        .map(item => item.product),
                })
            )
        );
    }
}
